import React, { useCallback, useEffect, useState } from 'react';
import {
  AlertTriangle, ChevronRight,
  KeyRound, Plus, Wifi, WifiOff, X,
} from 'lucide-react';

import { useXomicsAuth } from './AuthContext';
import {
  decommissionClusterNode, loadClusterFleet, loadClusterNode, loadJoinTokens,
  mintJoinToken, revokeJoinToken, updateClusterNode,
} from './api';
import {
  Empty, Failed, Head, Loading, Meter, Pill, SummaryBar, SummaryItem,
  ViewSwitch, XomicsSelect,
} from './parts';
import { errorMessage } from './ui';
import { useLiveResource } from './useLive';
import { tx } from './i18n';
import { codeLabel, statusLabel } from './labels';

function ago(value) {
  if (!value) return tx('ui.common.never');
  const seconds = Math.max(0, Math.round((Date.now() - Number(value)) / 1000));
  if (seconds < 60) return tx('ui.common.seconds_ago', { count: seconds });
  if (seconds < 3600) return tx('ui.common.minutes_ago', { count: Math.round(seconds / 60) });
  if (seconds < 86400) return tx('ui.common.hours_ago', { count: Math.round(seconds / 3600) });
  return new Date(Number(value)).toLocaleString(undefined, { day: '2-digit', month: 'short', year: 'numeric' });
}

function CopyButton({ value }) {
  const [copied, setCopied] = useState(false);
  async function copy() {
    try {
      if (navigator.clipboard && window.isSecureContext) await navigator.clipboard.writeText(value);
      else {
        const field = document.createElement('textarea');
        field.value = value;
        field.style.position = 'fixed';
        field.style.opacity = '0';
        document.body.appendChild(field);
        field.select();
        document.execCommand('copy');
        document.body.removeChild(field);
      }
      setCopied(true);
      window.setTimeout(() => setCopied(false), 1800);
    } catch (_error) {
      setCopied(false);
    }
  }
  return <button type="button" className="xo-btn-2" onClick={copy}>{copied ? tx("ui.cluster.copied_8e3df45a") : tx("ui.cluster.copy_command_a8e10478")}</button>;
}

function NodeCard({ node, onOpen }) {
  const fingerprint = node.fingerprint || {};
  const imageCount = Array.isArray(fingerprint.images) ? fingerprint.images.length : 0;
  const refCount = Array.isArray(fingerprint.refs) ? fingerprint.refs.length : 0;
  const ready = node.status === 'online' && node.is_enabled && !node.drift?.drifted;
  const usedDisk = Math.max(0, Number(node.storage_total_gb || 0) - Number(node.storage_free_gb || 0));
  return (
    <button type="button" className={`xo-node-card${ready ? ' is-ready' : ''}`} onClick={onOpen}>
      <span className="xo-node-card-head">
        <span className={`xo-node-state t-${ready ? 'ok' : node.status === 'decommissioned' ? 'flat' : 'warn'}`} aria-hidden="true">
          {ready ? <Wifi /> : <WifiOff />}
        </span>
        <span className="xo-lines"><strong>{node.display_name || node.hostname}</strong>
          <span>{node.display_name ? node.hostname : tx("ui.cluster.role_node_ee8bcc91", { role: codeLabel('nodeRole', node.role) })} · {node.arch || tx("ui.cluster.unknown_architecture_ade976ab")}</span></span>
        <span className="xo-node-pills">
          <Pill tone={node.status === 'online' ? 'ok' : node.status === 'decommissioned' ? 'flat' : 'warn'}>{statusLabel(node.status)}</Pill>
          <Pill tone={node.drift?.drifted ? 'bad' : 'ok'}>{node.drift?.drifted ? tx("ui.cluster.drifted_3284b46e") : tx("ui.cluster.converged_02ec64e3")}</Pill>
          {!node.is_enabled && <Pill tone="warn">{tx("ui.cluster.disabled_f4f4473d")}</Pill>}
        </span>
      </span>

      <span className="xo-node-release">
        <span><small>{tx("ui.cluster.target_release_505033cd")}</small><strong>{node.target_release_tag || tx("ui.cluster.none_6eef6648")}</strong></span>
        <span><small>{tx("ui.cluster.code_adac6937")}</small><strong className="xo-node-code">{fingerprint.git_commit ? String(fingerprint.git_commit).slice(0, 12) : tx("ui.cluster.not_reported_98e85682")}</strong></span>
        <span><small>{tx("ui.cluster.heartbeat_eb4d4196")}</small><strong>{ago(node.last_heartbeat_at)}</strong></span>
        <span><small>{tx("ui.cluster.active_work_01504932")}</small><strong>{tx("ui.cluster.value_jobvalue2_e8aee712", { value: Number(node.running_jobs || 0), value2: Number(node.running_jobs) === 1 ? '' : 's' })}</strong></span>
      </span>

      <span className="xo-node-capacity">
        <Meter label={tx("ui.cluster.cpu_threads_56bf097a")} value={Number(node.used_threads || 0)} total={Number(node.cpu_threads || 0)} tone="info" />
        <Meter label={tx("ui.cluster.memory_gb_5392e2da")} value={Number(node.used_ram_gb || 0)} total={Number(node.ram_gb || 0)} tone="warn" />
        <Meter label={tx("ui.cluster.disk_used_gb_2daf9d8c")} value={usedDisk} total={Number(node.storage_total_gb || 0)} tone={Number(node.storage_free_gb) < 200 ? 'bad' : 'flat'} />
      </span>

      <span className="xo-node-assets">
        <span>{tx("ui.cluster.imagecount_value_images_reported_06e15098", { imageCount: imageCount, value: Number(node.expected_image_count || 0) })}</span>
        <span>{tx("ui.cluster.refcount_value_reference_sets_reported_b46c2f96", { refCount: refCount, value: Number(node.expected_ref_count || 0) })}</span>
        <span>{node.gpu_count ? tx("ui.cluster.gpu_count_value_value2_gb_vram_cebb60a0", { gpu_count: node.gpu_count, value: node.gpu_model || 'GPU', value2: node.vram_gb || 0 }) : tx("ui.cluster.cpu_worker_058b428f")}</span>
      </span>
      {node.drift?.reasons?.length > 0 && <span className="xo-node-error"><AlertTriangle aria-hidden="true" />{node.drift.reasons.join(' · ')}</span>}
      {node.last_error && <span className="xo-node-error"><AlertTriangle aria-hidden="true" />{node.last_error}</span>}
      <ChevronRight className="xo-node-chevron" aria-hidden="true" />
    </button>
  );
}

function NodeDetail({ id, mayManage, onClose, onChanged }) {
  const load = useCallback(() => loadClusterNode(id), [id]);
  const resource = useLiveResource(['cluster'], load);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [confirming, setConfirming] = useState(false);

  useEffect(() => {
    const close = (event) => { if (event.key === 'Escape') onClose(); };
    window.addEventListener('keydown', close);
    return () => window.removeEventListener('keydown', close);
  }, [onClose]);

  async function changeEnabled(node) {
    setBusy(true); setError('');
    try {
      await updateClusterNode(node.id, { is_enabled: !node.is_enabled });
      await resource.refresh(); onChanged();
    } catch (caught) { setError(errorMessage(caught)); } finally { setBusy(false); }
  }

  async function decommission(node) {
    setBusy(true); setError('');
    try {
      await decommissionClusterNode(node.id);
      onChanged(); onClose();
    } catch (caught) { setError(errorMessage(caught)); setBusy(false); }
  }

  const detail = resource.data;
  return (
    <div className="xo-pipeline-modal-backdrop" role="presentation" onMouseDown={(event) => {
      if (event.target === event.currentTarget) onClose();
    }}>
      <section className="xo-node-modal" role="dialog" aria-modal="true" aria-labelledby="node-title">
        {resource.loading && detail === null ? <Loading label={tx("ui.cluster.reading_node_state_9bda2dc0")} />
          : resource.error && detail === null ? <Failed message={resource.error} onRetry={resource.refresh} />
            : (() => {
              const { node } = detail;
              const fingerprint = node.fingerprint || {};
              const facts = [
                [tx('ui.cluster.fact_node_id'), node.id],
                [tx('ui.cluster.fact_role'), codeLabel('nodeRole', node.role)],
                [tx('ui.cluster.fact_address'), node.ip_address || '—'],
                [tx('ui.cluster.fact_provider_region'), [node.provider, node.region].filter(Boolean).join(' · ') || tx('ui.cluster.not_reported_98e85682')],
                [tx('ui.cluster.fact_architecture'), node.arch || '—'],
                [tx('ui.cluster.fact_operating_system'), node.os_version || '—'],
                [tx('ui.cluster.fact_cpu'), tx('ui.cluster.cpu_description', {
                  model: node.cpu_model || tx('ui.common.unknown'),
                  cores: node.cpu_cores,
                  threads: node.cpu_threads,
                })],
                [tx('ui.cluster.fact_memory'), tx('ui.cluster.memory_gigabytes', { value: node.ram_gb })],
                [tx('ui.cluster.fact_gpu'), node.gpu_count
                  ? tx('ui.cluster.gpu_description', {
                    count: node.gpu_count,
                    model: node.gpu_model || 'GPU',
                    memory: node.vram_gb || 0,
                  }) : tx('ui.cluster.none_6eef6648')],
                [tx('ui.cluster.fact_disk'), tx('ui.cluster.disk_description', {
                  free: node.storage_free_gb,
                  total: node.storage_total_gb,
                })],
                [tx('ui.cluster.fact_reservations'), tx('ui.cluster.reservations_description', {
                  threads: node.used_threads,
                  memory: node.used_ram_gb,
                  disk: node.reserved_storage_gb,
                })],
                [tx('ui.cluster.fact_work_root'), node.work_root || '—'],
                [tx('ui.cluster.fact_agent'), node.agent_version || '—'],
                [tx('ui.cluster.fact_target'), node.target_release_tag
                  ? tx('ui.cluster.release_target', { tag: node.target_release_tag, id: node.target_release_id })
                  : tx('ui.cluster.no_release')],
                [tx('ui.cluster.fact_fingerprint_time'), ago(node.fingerprint_at)],
                [tx('ui.cluster.fact_heartbeat'), ago(node.last_heartbeat_at)],
                [tx('ui.cluster.fact_registered'), ago(node.created_at)],
                [tx('ui.cluster.fact_last_control_update'), ago(node.updated_at)],
              ];
              return <>
                <header className="xo-pipeline-modal-head"><div>
                  <span className="xo-pipeline-modal-flags"><Pill tone={node.status === 'online' ? 'ok' : 'flat'}>{statusLabel(node.status)}</Pill>
                    <Pill tone={node.drift?.drifted ? 'bad' : 'ok'}>{node.drift?.drifted ? tx("ui.cluster.drifted_3284b46e") : tx("ui.cluster.converged_02ec64e3")}</Pill></span>
                  <h2 id="node-title">{node.display_name || node.hostname}</h2><p>{node.hostname}</p>
                </div><button type="button" className="xo-btn-icon" aria-label={tx("ui.cluster.close_node_ddfe8f27")} onClick={onClose}><X aria-hidden="true" /></button></header>
                <div className="xo-node-modal-scroll">
                  {(error || resource.error) && <p className="xo-alert t-bad">{error || resource.error}</p>}
                  {node.last_error && <p className="xo-alert t-bad"><strong>{tx("ui.cluster.last_agent_error_e0924dac")}</strong> {node.last_error}</p>}
                  {node.notes && <p className="xo-alert t-info"><strong>{tx("ui.cluster.operator_notes_49749a7a")}</strong> {node.notes}</p>}
                  {node.drift?.reasons?.length > 0 && <div className="xo-alert t-warn"><strong>{tx("ui.cluster.why_this_node_is_withheld_b54e104c")}</strong><ul>{node.drift.reasons.map((reason) => <li key={reason}>{reason}</li>)}</ul></div>}
                  <dl className="xo-node-facts">{facts.map(([label, value]) => <div key={label}><dt>{label}</dt><dd>{value}</dd></div>)}</dl>

                  <section className="xo-node-detail-section"><h3>{tx("ui.cluster.reported_code_and_assets_20f5fd18")}</h3>
                    <div className="xo-node-fingerprint"><span><small>{tx("ui.cluster.commit_1a1873ab")}</small><strong className="xo-mono">{fingerprint.git_commit || tx("ui.cluster.not_reported_98e85682")}</strong></span>
                      <span><small>{tx("ui.cluster.locally_modified_files_00733422")}</small><strong>{Number(fingerprint.dirty || 0)}</strong></span></div>
                    <div className="xo-node-assets-detail"><div><h4>{tx("ui.cluster.expected_images_2106970c")}</h4>{detail.expected_images.length === 0 ? <p className="xo-muted">{tx("ui.cluster.none_6eef6648")}</p>
                      : detail.expected_images.map((image) => <span className="xo-chip" key={image}>{image}</span>)}</div>
                      <div><h4>{tx("ui.cluster.reported_images_1fff55c2")}</h4>{!Array.isArray(fingerprint.images) || fingerprint.images.length === 0 ? <p className="xo-muted">{tx("ui.cluster.none_6eef6648")}</p>
                        : fingerprint.images.map((image) => <span className="xo-chip" key={image.tag}>{image.tag}<small>{image.id ? tx("ui.cluster.value_92e0fa78", { value: String(image.id).slice(0, 18) }) : ''}</small></span>)}</div>
                      <div><h4>{tx("ui.cluster.expected_references_1ddfa9cc")}</h4>{detail.expected_refs.length === 0 ? <p className="xo-muted">{tx("ui.cluster.none_6eef6648")}</p>
                        : detail.expected_refs.map((reference) => <span className="xo-chip" key={reference}>{reference}</span>)}</div>
                      <div><h4>{tx("ui.cluster.reported_references_e9a4c66c")}</h4>{!Array.isArray(fingerprint.refs) || fingerprint.refs.length === 0 ? <p className="xo-muted">{tx("ui.cluster.none_6eef6648")}</p>
                        : fingerprint.refs.map((reference) => <span className="xo-chip" key={reference}>{reference}</span>)}</div></div>
                  </section>

                  <section className="xo-node-detail-section"><h3>{tx("ui.cluster.active_jobs_69ad6de4")}</h3>
                    {detail.active_jobs.length === 0 ? <Empty title={tx("ui.cluster.no_work_on_this_node_86f4eeed")} detail={tx("ui.cluster.the_worker_is_currently_idle_b2ecaf65")} />
                      : <div className="xo-table-wrap"><table className="xo-table"><thead><tr><th>{tx("ui.cluster.job_30c8cb83")}</th><th>{tx("ui.cluster.pipeline_32b1d5a7")}</th><th>{tx("ui.cluster.status_bae7d5be")}</th><th>{tx("ui.cluster.cpu_ff221d47")}</th><th>{tx("ui.cluster.ram_ac698b84")}</th><th>{tx("ui.cluster.disk_ef995260")}</th><th>{tx("ui.cluster.started_faa9e7e7")}</th></tr></thead>
                        <tbody>{detail.active_jobs.map((job) => <tr key={job.id}><td className="xo-mono">{String(job.uuid).slice(0, 12)}</td>
                          <td><strong>{job.display_name}</strong><small className="xo-cell-note">{job.mode || tx("ui.cluster.default_7505d64a")} · v{job.code_version}</small></td>
                          <td><Pill tone="info">{statusLabel(job.run_status)}</Pill></td><td>{job.req_threads}</td><td>{tx("ui.cluster.req_ram_gb_gb_ec1ede94", { req_ram_gb: job.req_ram_gb })}</td><td>{tx("ui.cluster.req_storage_gb_gb_1121c638", { req_storage_gb: job.req_storage_gb })}</td><td>{ago(job.run_started_at || job.assigned_at)}</td></tr>)}</tbody></table></div>}
                  </section>

                  {mayManage && node.status !== 'decommissioned' && <section className="xo-node-manage"><div><h3>{tx("ui.cluster.node_controls_c71353d5")}</h3><p>{tx("ui.cluster.changes_set_control_plane_intent_the_node_observes_the_964aa82c")}</p></div>
                    <div className="xo-bar-group"><button type="button" className="xo-btn-2" disabled={busy} onClick={() => changeEnabled(node)}>{node.is_enabled ? tx("ui.cluster.disable_scheduling_36e1e334") : tx("ui.cluster.enable_scheduling_d4233359")}</button>
                      {!confirming ? <button type="button" className="xo-link t-bad" disabled={busy} onClick={() => setConfirming(true)}>{tx("ui.cluster.decommission_c05e3062")}</button>
                        : <span className="xo-confirm-inline"><strong>{tx("ui.cluster.revoke_this_node_permanently_2bd229b4")}</strong><button type="button" className="xo-btn-2" onClick={() => setConfirming(false)}>{tx("ui.cluster.cancel_77dfd213")}</button>
                          <button type="button" className="xo-danger-button" disabled={busy} onClick={() => decommission(node)}>{tx("ui.cluster.confirm_04a21221")}</button></span>}</div></section>}
                </div>
              </>;
            })()}
      </section>
    </div>
  );
}

function JoinNodes({ onClose, onChanged }) {
  const load = useCallback(() => loadJoinTokens(), []);
  const tokens = useLiveResource(['cluster'], load);
  const [role, setRole] = useState('worker');
  const [minted, setMinted] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');

  async function mint() {
    setBusy(true); setError('');
    try { setMinted(await mintJoinToken({ role, ttlSeconds: 900 })); tokens.refresh(); onChanged(); }
    catch (caught) { setError(errorMessage(caught)); } finally { setBusy(false); }
  }
  async function revoke(id) {
    setError('');
    try { await revokeJoinToken(id); tokens.refresh(); onChanged(); }
    catch (caught) { setError(errorMessage(caught)); }
  }

  return <div className="xo-pipeline-modal-backdrop" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) onClose(); }}>
    <section className="xo-join-modal" role="dialog" aria-modal="true" aria-labelledby="join-title">
      <header className="xo-pipeline-modal-head"><div><span className="xo-pipeline-modal-flags"><Pill tone="info">{tx("ui.cluster.single_use_532ca4e0")}</Pill></span><h2 id="join-title">{tx("ui.cluster.add_a_cluster_node_7c5e6667")}</h2>
        <p>{tx("ui.cluster.join_tokens_expire_after_15_minutes_and_carry_the_node_64358a97")}</p></div><button type="button" className="xo-btn-icon" aria-label={tx("ui.cluster.close_add_node_b2aa11b2")} onClick={onClose}><X /></button></header>
      <div className="xo-node-modal-scroll">
        {error && <p className="xo-alert t-bad">{error}</p>}
        <div className="xo-join-create"><label><span>{tx("ui.cluster.role_c3f104d1")}</span><XomicsSelect value={role} onChange={(event) => setRole(event.target.value)} disabled={busy}><option value="worker">{tx("ui.cluster.worker_99edd8c8")}</option><option value="storage">{tx("ui.cluster.storage_9e092dda")}</option></XomicsSelect></label>
          <button type="button" className="xo-btn" disabled={busy} onClick={mint}><KeyRound aria-hidden="true" />{busy ? tx("ui.cluster.generating_11edad70") : tx("ui.cluster.generate_join_command_3450bf98")}</button></div>
        {minted && <div className="xo-join-command"><p>{tx("ui.cluster.run_once_on_the_new_role_this_command_cannot_be_retrie_e00cf54f", { role: codeLabel('nodeRole', minted.role) })}</p><pre>{minted.join_command}</pre><CopyButton value={minted.join_command} /></div>}
        <section className="xo-node-detail-section"><h3>{tx("ui.cluster.recent_tokens_128e56a9")}</h3>
          {tokens.loading && tokens.data === null ? <Loading label={tx("ui.cluster.reading_join_tokens_1000c5c7")} /> : tokens.error && tokens.data === null ? <Failed message={tokens.error} onRetry={tokens.refresh} />
            : !tokens.data || tokens.data.length === 0 ? <Empty title={tx("ui.cluster.no_join_tokens_e04ab303")} /> : <div className="xo-table-wrap"><table className="xo-table"><thead><tr><th>{tx("ui.cluster.role_c3f104d1")}</th><th>{tx("ui.cluster.state_a7250206")}</th><th>{tx("ui.cluster.issued_by_a8fac411")}</th><th>{tx("ui.cluster.redeemed_node_a209b2b9")}</th><th>{tx("ui.cluster.created_accf40c8")}</th><th /></tr></thead>
              <tbody>{tokens.data.map((token) => <tr key={token.id}><td>{codeLabel('nodeRole', token.role)}</td><td><Pill tone={token.state === 'pending' ? 'info' : token.state === 'redeemed' ? 'ok' : 'flat'}>{statusLabel(token.state)}</Pill></td>
                <td>{token.issued_by_email || '—'}</td><td>{token.redeemed_hostname || '—'}</td><td>{ago(token.created_at)}</td><td className="xo-actions">{token.state === 'pending' && <button type="button" className="xo-link t-bad" onClick={() => revoke(token.id)}>{tx("ui.cluster.revoke_0be72075")}</button>}</td></tr>)}</tbody></table></div>}
        </section>
      </div>
    </section>
  </div>;
}

export default function XomicsCluster() {
  const { permissions } = useXomicsAuth();
  const mayManage = permissions.includes('NODE_MANAGE');
  const load = useCallback(() => loadClusterFleet(), []);
  const fleet = useLiveResource(['cluster', 'pipelines', 'uploads'], load);
  const [scope, setScope] = useState('active');
  const [selectedId, setSelectedId] = useState(null);
  const [joining, setJoining] = useState(false);
  const [view, setView] = useState(() => {
    try { return window.localStorage.getItem('xo-cluster-view') === 'list' ? 'list' : 'grid'; }
    catch (_error) { return 'grid'; }
  });
  const data = fleet.data || { nodes: [], queue: { jobs: 0 } };
  const activeNodes = data.nodes.filter((node) => node.status !== 'decommissioned');
  const workers = activeNodes.filter((node) => node.role === 'worker');
  const nodes = scope === 'all' ? data.nodes : activeNodes;
  const ready = workers.filter((node) => node.status === 'online' && node.is_enabled && !node.drift?.drifted);
  const online = workers.filter((node) => node.status === 'online').length;
  const running = workers.reduce((sum, node) => sum + Number(node.running_jobs || 0), 0);
  const freeDisk = workers.reduce((sum, node) => sum + Number(node.storage_free_gb || 0), 0);

  if (fleet.loading && fleet.data === null) return <Loading label={tx("ui.cluster.reading_cluster_state_26c543c6")} />;
  if (fleet.error && fleet.data === null) return <Failed message={fleet.error} onRetry={fleet.refresh} />;

  function changeView(nextView) {
    setView(nextView);
    try { window.localStorage.setItem('xo-cluster-view', nextView); } catch (_error) { /* session-only */ }
  }

  return <div className="xo-platform-page">
    <Head title={tx("ui.cluster.cluster_d75dc68b")} subtitle={tx("ui.cluster.workers_storage_capacity_release_convergence_and_the_l_38944f2a")}>
      {mayManage && <button type="button" className="xo-btn" onClick={() => setJoining(true)}><Plus aria-hidden="true" /> {tx("ui.cluster.add_node_05790726")}</button>}
    </Head>
    {fleet.error && <p className="xo-alert t-warn">{fleet.error}</p>}
    <SummaryBar>
      <SummaryItem value={`${ready.length} / ${workers.length}`} label={tx("ui.cluster.ready_workers_bab8cf36")}
        tone={workers.length > 0 && ready.length === workers.length ? 'ok' : 'warn'}
        detail={tx("ui.cluster.online_reporting_value_withheld_or_offline_ec8fcdbc", { online: online, value: workers.length - ready.length })} />
      <SummaryItem value={running} label={tx("ui.cluster.running_jobs_ae696f7c")}
        detail={running > 0 ? tx("ui.cluster.assigned_downloading_or_running_f3dd587a") : tx("ui.cluster.fleet_idle_15b9872b")} />
      <SummaryItem value={data.queue.jobs || 0} label={tx("ui.cluster.queued_jobs_adb336b9")} tone={data.queue.jobs > 0 ? 'warn' : 'ok'}
        detail={data.queue.jobs > 0
          ? tx("ui.cluster.requested_threads_threads_requested_ram_gb_gb_ram_requ_3ba5d182", { requested_threads: data.queue.requested_threads, requested_ram_gb: data.queue.requested_ram_gb, requested_storage_gb: data.queue.requested_storage_gb })
          : tx("ui.cluster.nothing_waiting_c5cb3e55")} />
      <SummaryItem value={`${Math.round(freeDisk)} GB`} label={tx("ui.cluster.free_worker_disk_9b48ab08")} tone={freeDisk < 200 ? 'warn' : 'flat'}
        detail={tx("ui.cluster.across_active_workers_a0f76cf2")} />
    </SummaryBar>
    <div className="xo-cluster-toolbar"><div className="xo-seg" role="group" aria-label={tx("ui.cluster.node_history_484cd544")}>
      <button type="button" className={scope === 'active' ? 'is-on' : ''} onClick={() => setScope('active')}>{tx("ui.cluster.active_nodes_70d67307")}</button>
      <button type="button" className={scope === 'all' ? 'is-on' : ''} onClick={() => setScope('all')}>{tx("ui.cluster.include_decommissioned_b60f321b")}</button>
    </div><div className="xo-toolbar-end"><span>{tx("ui.cluster.length_nodevalue_4f2d3ba0", { length: nodes.length, value: nodes.length === 1 ? '' : 's' })}</span>
      <ViewSwitch value={view} onChange={changeView} label={tx("ui.cluster.cluster_layout_c4f1d1fe")} /></div></div>
    <div className={`xo-data-surface xo-node-list is-${view}`}>{nodes.length === 0 ? <Empty title={tx("ui.cluster.no_cluster_nodes_673f1f17")} detail={mayManage ? tx("ui.cluster.add_a_node_to_begin_d046972f") : tx("ui.cluster.no_nodes_are_visible_13fb77f9")} />
      : nodes.map((node) => <NodeCard key={node.id} node={node} onOpen={() => setSelectedId(node.id)} />)}</div>
    {selectedId !== null && <NodeDetail id={selectedId} mayManage={mayManage} onClose={() => setSelectedId(null)} onChanged={fleet.refresh} />}
    {joining && <JoinNodes onClose={() => setJoining(false)} onChanged={fleet.refresh} />}
  </div>;
}
